import { seededRandom, shade } from '../pixel.js';
import { stamp } from '../smooth.js';
import type { PixelCanvas } from '../pixel.js';
import {
  HH,
  HW,
  box,
  drawBox,
  drawBoxes,
  drawCylinder,
  faceQuad,
  faces,
  frontFace,
  iso,
  seatWithBack,
} from './iso.js';
import type { BoxPiece, Face, Faces, FurniDrawer } from './iso.js';

/**
 * Singapore-themed decorations and the kopitiam cafe set. Owned by the
 * art feature. Keys are furniture kinds from protocol furniture/singapore.ts.
 */

/* -------------------------------------------------------------------------- */
/* Small helpers                                                              */
/* -------------------------------------------------------------------------- */

/** 3x5 capitals and digits for signboards. */
const FONT: Record<string, readonly string[]> = {
  A: ['.#.', '#.#', '###', '#.#', '#.#'],
  E: ['###', '#..', '##.', '#..', '###'],
  I: ['###', '.#.', '.#.', '.#.', '###'],
  K: ['#.#', '##.', '#..', '##.', '#.#'],
  M: ['#.#', '###', '###', '#.#', '#.#'],
  N: ['##.', '#.#', '#.#', '#.#', '#.#'],
  O: ['###', '#.#', '#.#', '#.#', '###'],
  P: ['##.', '#.#', '##.', '#..', '#..'],
  T: ['###', '.#.', '.#.', '.#.', '.#.'],
  U: ['#.#', '#.#', '#.#', '#.#', '###'],
  $: ['.#.', '##.', '.#.', '.##', '.#.'],
  '1': ['.#.', '##.', '.#.', '.#.', '###'],
  '0': ['###', '#.#', '#.#', '#.#', '###'],
  '~': ['...', '...', '#.#', '.#.', '...'],
};

function text(pc: PixelCanvas, value: string, x: number, y: number, color: string): void {
  // One bitmap for the whole line, so letters are smoothed as shapes.
  const rows = Array.from({ length: 5 }, (_, ry) =>
    [...value].map((ch) => FONT[ch]?.[ry] ?? '...').join('.'),
  );
  stamp(pc, rows, (c) => (c === '#' ? color : undefined), x, y);
}

/** Axis a long, thin item runs along for a rotation: facing ±v means along u. */
const runsAlongU = (rotation: number) => rotation === 0 || rotation === 4;

/** A box spanning the full tile along the run axis and `half` across it. */
function runBox(rotation: number, half: number, h0: number, h1: number, colors: Faces, span = 0.5): BoxPiece {
  return runsAlongU(rotation)
    ? box(-span, span, -half, half, h0, h1, colors)
    : box(-half, half, -span, span, h0, h1, colors);
}

/** The backrest of a run-style seat, on the side opposite its facing. */
function backRest(rotation: number, half: number, thickness: number, h0: number, h1: number, colors: Faces, span = 0.5): BoxPiece {
  const t = half - thickness;
  switch (rotation) {
    case 2:
      return box(-half, -t, -span, span, h0, h1, colors);
    case 6:
      return box(t, half, -span, span, h0, h1, colors);
    case 4:
      return box(-span, span, -half, -t, h0, h1, colors);
    default:
      return box(-span, span, t, half, h0, h1, colors);
  }
}

/** An armrest at one end of the run axis (-1 or +1). */
function armRest(rotation: number, end: -1 | 1, half: number, h0: number, h1: number, colors: Faces): BoxPiece {
  const a = end < 0 ? -0.5 : 0.36;
  const b = end < 0 ? -0.36 : 0.5;
  return runsAlongU(rotation) ? box(a, b, -half, half, h0, h1, colors) : box(-half, half, a, b, h0, h1, colors);
}

/** Random dots of colour, only on pixels already painted (terrazzo, mosaic, leaves). */
function sprinkle(pc: PixelCanvas, seed: number, x0: number, y0: number, w: number, h: number, colors: readonly string[], count: number): void {
  const rand = seededRandom(seed);
  for (let i = 0; i < count; i += 1) {
    const x = x0 + Math.floor(rand() * w);
    const y = y0 + Math.floor(rand() * h);
    const color = colors[Math.floor(rand() * colors.length)]!;
    if (pc.alphaAt(x + pc.ox, y + pc.oy) === 255) pc.px(x, y, color);
  }
}

/* -------------------------------------------------------------------------- */
/* The cafe set                                                               */
/* -------------------------------------------------------------------------- */

const COUCH = faces('#e07a2e', '#f39a52');
const COUCH_BACK = faces('#c9661f', '#e8843a');

function couch(pc: PixelCanvas, rotation: number, arms: { neg: boolean; pos: boolean }): void {
  const pieces: BoxPiece[] = [
    runBox(rotation, 0.42, 0, 14, COUCH),
    backRest(rotation, 0.42, 0.15, 14, 34, COUCH_BACK),
  ];
  if (arms.neg) pieces.push(armRest(rotation, -1, 0.42, 14, 24, COUCH_BACK));
  if (arms.pos) pieces.push(armRest(rotation, 1, 0.42, 14, 24, COUCH_BACK));
  drawBoxes(pc, pieces);
  // Cushion seam across the seat.
  const seam = runsAlongU(rotation) ? [iso(0, -0.2, 14), iso(0, 0.3, 14)] : [iso(-0.2, 0, 14), iso(0.3, 0, 14)];
  pc.line(seam[0]!.x, seam[0]!.y, seam[1]!.x, seam[1]!.y, '#c9661f');
}

const drawCouchLeft: FurniDrawer = (pc, r) => couch(pc, r, { neg: true, pos: false });
const drawCouchMiddle: FurniDrawer = (pc, r) => couch(pc, r, { neg: false, pos: false });
const drawCouchRight: FurniDrawer = (pc, r) => couch(pc, r, { neg: false, pos: true });

const drawBarStool: FurniDrawer = (pc) => {
  drawCylinder(pc, 0.18, 0, 2, '#8b9299', '#6b727a');
  drawCylinder(pc, 0.04, 2, 22, '#c7cdd2', '#a9b0b7');
  const ring = iso(0, 0, 9);
  pc.rect(ring.x - 7, ring.y, 14, 1, '#a9b0b7');
  drawCylinder(pc, 0.2, 22, 26, '#ea5a4f', '#b8352e');
};

const drawCoffeeTable: FurniDrawer = (pc) => {
  const wood = faces('#8a5a34', '#b07a48');
  const legs = [
    [-0.36, -0.36],
    [0.3, -0.36],
    [0.3, 0.3],
    [-0.36, 0.3],
  ].map(([u, v]) => box(u!, u! + 0.06, v!, v! + 0.06, 0, 11, faces('#6b4226')));
  drawBoxes(pc, legs);
  drawBox(pc, box(-0.42, 0.42, -0.42, 0.42, 11, 15, wood));
  // A magazine and a big mug.
  const mag = iso(-0.12, 0.08, 15);
  pc.poly([iso(-0.28, -0.05, 15), iso(0.02, -0.05, 15), iso(0.02, 0.2, 15), iso(-0.28, 0.2, 15)], '#3a6fd6');
  pc.px(mag.x, mag.y, '#fffaf0');
  const mug = iso(0.2, -0.15, 15);
  pc.rect(mug.x - 2, mug.y - 5, 5, 5, '#f4efe4');
  pc.rect(mug.x - 2, mug.y - 5, 5, 1, '#6b4226');
  pc.rect(mug.x + 3, mug.y - 4, 1, 2, '#dcd5c6');
};

const drawCafeWindow: FurniDrawer = (pc, rotation) => {
  const alongU = runsAlongU(rotation);
  const frame = faces('#f3ead8', '#fffaf0');
  const t = 0.08;
  drawBox(pc, alongU ? box(-0.5, 0.5, -t, t, 0, 74, frame) : box(-t, t, -0.5, 0.5, 0, 74, frame));
  const face: Face = alongU ? 'left' : 'right';
  // Brick base, glass, mullions.
  faceQuad(pc, face, t, -0.5, 0.5, 0, 18, '#b5563f');
  for (let h = 3; h < 18; h += 5) faceQuad(pc, face, t, -0.5, 0.5, h, h + 1, '#9c4533');
  faceQuad(pc, face, t, -0.42, 0.42, 22, 62, '#9fd3ea');
  faceQuad(pc, face, t, -0.42, -0.2, 50, 62, '#c9e8f5');
  faceQuad(pc, face, t, -0.03, 0.03, 22, 62, '#f3ead8');
  faceQuad(pc, face, t, -0.42, 0.42, 41, 43, '#f3ead8');
  // A plant on the sill, seen through the glass.
  faceQuad(pc, face, t, 0.2, 0.32, 22, 28, '#c0643a');
  faceQuad(pc, face, t, 0.17, 0.35, 28, 34, '#3d9e4c');
  // Striped awning.
  for (let i = 0; i < 8; i += 1) {
    const s0 = -0.5 + i / 8;
    faceQuad(pc, face, t, s0, s0 + 1 / 8, 66, 74, i % 2 ? '#fffaf0' : '#d9433b');
  }
};

const drawStageFloor: FurniDrawer = (pc) => {
  const d = (r: number, color: string) => pc.poly([iso(-r, -r), iso(r, -r), iso(r, r), iso(-r, r)], color);
  d(0.5, '#6b4226');
  d(0.46, '#b07a48');
  for (let i = -3; i <= 3; i += 1) {
    const s = i * 0.13;
    const a = iso(s, -0.46);
    const b = iso(s, 0.46);
    pc.line(a.x, a.y, b.x, b.y, '#8a5a34');
  }
};

const drawMicStand: FurniDrawer = (pc, rotation) => {
  drawCylinder(pc, 0.15, 0, 2, '#3a3a42', '#2a2a33');
  pc.rect(-1, -52, 2, 50, '#5a5a62');
  pc.rect(-1, -52, 1, 50, '#8a8a92');
  pc.line(0, -52, 6, -56, '#5a5a62');
  pc.ellipse(8, -57, 3, 3.5, '#2a2a33');
  pc.px(7, -58, '#8a8a92');
  // Cable trailing off.
  pc.line(1, -40, 4, -2, '#1d1d1d');
  // Mirror the mic's lean and cable to the other side when the stand turns to face right.
  if (frontFace(rotation) === 'right') pc.flipX();
};

const drawChalkboard: FurniDrawer = (pc, rotation) => {
  const alongU = runsAlongU(rotation);
  const wood = faces('#8a5a34');
  drawBox(pc, alongU ? box(-0.38, 0.38, -0.06, 0.06, 0, 56, wood) : box(-0.06, 0.06, -0.38, 0.38, 0, 56, wood));
  const face: Face = alongU ? 'left' : 'right';
  faceQuad(pc, face, 0.06, -0.33, 0.33, 12, 52, '#2f4a3a');
  // Chalk: a heading and a few lines of "menu".
  faceQuad(pc, face, 0.06, -0.26, 0.2, 44, 47, '#fffaf0');
  for (const [s, h, w] of [
    [-0.26, 36, 0.4],
    [-0.26, 30, 0.3],
    [-0.26, 24, 0.44],
    [-0.26, 18, 0.25],
  ] as const) {
    faceQuad(pc, face, 0.06, s, s + w, h, h + 2, h === 30 ? '#f0bf36' : '#e6efe6');
  }
};

/* -------------------------------------------------------------------------- */
/* Singapore icons                                                            */
/* -------------------------------------------------------------------------- */

const drawMerlion: FurniDrawer = (pc, rotation) => {
  const stone = '#faf5ea';
  const light = '#ffffff';
  const shadow = '#ddd4c2';
  const deep = '#b0a48b';
  const mane = '#cfc4ab';
  const maneDark = '#8f8267';
  const rockTop = '#aab1b8';
  const rockSide = '#7b838b';
  const waterA = '#5fa8d6';
  const waterB = '#a9dcf1';
  const spray = '#e8f8fd';

  /** A slice of stone: cool shadow on the right, warm light on the left. */
  const blob = (cx: number, cy: number, rx: number, ry: number, glint = true) => {
    pc.ellipse(cx + 1.2, cy, rx, ry, deep);
    pc.ellipse(cx + 0.4, cy, rx - 0.8, ry - 0.2, shadow);
    pc.ellipse(cx - 1.4, cy - 0.3, Math.max(1, rx - 3.4), Math.max(1, ry - 0.7), stone);
    if (glint) pc.ellipse(cx - rx * 0.5, cy - 0.5, Math.max(1, rx * 0.2), 1.4, light);
  };

  // Pool with ripples.
  pc.ellipse(0, 2, 31, 15.5, '#3f86b8');
  pc.ellipse(0, 1, 29, 14, waterA);
  pc.ellipse(-2, 0, 23, 10.5, '#78bce0');
  for (const [x, y, w] of [
    [-22, 3, 5],
    [14, 6, 6],
    [-8, 9, 7],
    [10, -3, 4],
  ] as const) {
    pc.rect(x, y, w, 1, waterB);
  }

  // Rocky plinth: two stacked stones.
  drawCylinder(pc, 0.24, 0, 7, rockTop, rockSide);
  drawCylinder(pc, 0.18, 7, 12, '#c2c9cf', rockSide, 0.02, -0.02);
  pc.rect(-9, -4, 3, 1, '#c2c9cf');
  pc.rect(3, -9, 4, 1, '#96a0a8');

  // Tail: curls up behind the body on the right and ends in a two-lobed fluke.
  for (let i = 0; i <= 26; i += 1) {
    const t = i / 26;
    blob(6 + 17 * Math.sin(t * 1.55), -14 - 27 * t, 6 - 3.2 * t, 5.2 - 2.8 * t);
  }
  const fx = 6 + 17 * Math.sin(1.55);
  const fy = -41;
  pc.poly([{ x: fx + 1, y: fy + 3 }, { x: fx - 13, y: fy - 4 }, { x: fx - 11, y: fy - 15 }, { x: fx + 1, y: fy - 9 }], shadow);
  pc.poly([{ x: fx + 1, y: fy + 3 }, { x: fx - 12, y: fy - 5 }, { x: fx - 10, y: fy - 13 }, { x: fx + 1, y: fy - 8 }], stone);
  pc.poly([{ x: fx + 1, y: fy + 3 }, { x: fx + 14, y: fy - 4 }, { x: fx + 12, y: fy - 15 }, { x: fx + 2, y: fy - 9 }], deep);
  pc.poly([{ x: fx + 1, y: fy + 3 }, { x: fx + 12, y: fy - 5 }, { x: fx + 10, y: fy - 12 }, { x: fx + 2, y: fy - 8 }], shadow);

  // Body: a fat fish belly narrowing into a leaning neck.
  for (let i = 0; i <= 40; i += 1) {
    const t = i / 40;
    blob(-2 + 5 * Math.sin(t * 2.8), -11 - 33 * t, 15.5 - 8 * t + 2 * Math.sin(t * Math.PI), 4.4, false);
  }
  // Side fin.
  pc.poly([{ x: 10, y: -22 }, { x: 18, y: -26 }, { x: 16, y: -14 }, { x: 11, y: -12 }], deep);
  pc.poly([{ x: 10, y: -22 }, { x: 16, y: -25 }, { x: 14, y: -16 }, { x: 11, y: -14 }], shadow);
  // Scales: bold "u" arcs in staggered rows, only on painted body pixels.
  const ok = (px: number, py: number) => pc.alphaAt(px + pc.ox, py + pc.oy) === 255;
  for (let row = 0; row < 7; row += 1) {
    for (let col = -3; col <= 3; col += 1) {
      const x = col * 5 + (row % 2) * 2.5 - 1 + row * 0.3;
      const y = -14 - row * 4;
      if (ok(x - 2, y) && ok(x + 2, y + 2)) {
        pc.px(x - 2, y, deep);
        pc.px(x - 1, y + 1, deep);
        pc.px(x, y + 2, deep);
        pc.px(x + 1, y + 1, deep);
        pc.px(x + 2, y, deep);
      }
    }
  }
  pc.rect(-14, -11, 24, 1, deep);

  // Lion head, three-quarter view facing left, with a big scalloped mane.
  const hx = -1;
  const hy = -58;
  const ring = (r: number, size: number, count: number, turn: number, color: string) => {
    for (let i = 0; i < count; i += 1) {
      const a = (i / count) * Math.PI * 2 + turn;
      pc.ellipse(hx + 3 + Math.cos(a) * r, hy + Math.sin(a) * r, size, size, color);
    }
  };
  ring(13.5, 4.4, 13, 0, maneDark);
  ring(12.5, 4.2, 13, 0.24, mane);
  ring(9, 4.6, 9, 0.4, mane);
  ring(6, 4.2, 6, 0.9, '#dcd3bd');
  // Curl marks in the mane.
  for (let i = 0; i < 9; i += 1) {
    const a = (i / 9) * Math.PI * 2 + 0.1;
    const cx = Math.round(hx + 3 + Math.cos(a) * 9);
    const cy = Math.round(hy + Math.sin(a) * 9);
    pc.px(cx, cy, maneDark);
    pc.px(cx + 1, cy, maneDark);
    pc.px(cx - 1, cy - 1, '#ece5d3');
  }
  // Ears.
  pc.ellipse(hx + 4, hy - 16, 3.6, 3.6, maneDark);
  pc.ellipse(hx + 4, hy - 16, 2, 2, '#e8b9a6');
  // Face.
  pc.ellipse(hx - 1, hy + 1, 10, 10, shadow);
  pc.ellipse(hx - 2, hy + 1, 9, 9.5, stone);
  pc.ellipse(hx - 5, hy - 3, 3.2, 2.2, light);
  // Brow ridge and eye.
  pc.rect(hx - 10, hy - 5, 8, 2, shadow);
  pc.rect(hx - 8, hy - 3, 4, 4, light);
  pc.rect(hx - 7, hy - 2, 3, 3, '#26262e');
  pc.px(hx - 7, hy - 2, light);
  // Snout and roaring mouth.
  pc.ellipse(hx - 11, hy + 3, 6.5, 4, stone);
  pc.rect(hx - 17, hy + 1, 6, 2, '#26262e');
  pc.rect(hx - 17, hy + 1, 3, 1, '#26262e');
  pc.rect(hx - 17, hy + 4, 12, 6, '#7a2b26');
  pc.rect(hx - 17, hy + 4, 12, 1, stone);
  pc.rect(hx - 16, hy + 5, 1, 2, light);
  pc.rect(hx - 12, hy + 5, 1, 2, light);
  pc.rect(hx - 15, hy + 8, 8, 2, '#d9605a');
  pc.ellipse(hx - 9, hy + 11, 6, 2.6, stone);
  pc.rect(hx - 15, hy + 10, 8, 1, shadow);
  pc.rect(hx - 4, hy + 4, 3, 2, '#f0b8a8');

  // Water arcing from the mouth into the pool.
  const mx = hx - 18;
  const my = hy + 8;
  for (let i = 0; i <= 40; i += 1) {
    const t = i / 40;
    const x = mx - t * 10;
    const y = my - 2 * Math.sin(Math.min(1, t * 2) * Math.PI * 0.5) + 50 * t * t;
    pc.rect(x, y, 3, 2, i % 3 === 0 ? spray : waterB);
    pc.px(x + 2, y + 1, waterA);
    pc.px(x, y + 2, waterA);
  }
  for (const [x, y] of [[-27, 4], [-24, 0], [-21, 3], [-30, 6], [-19, 5]] as const) pc.px(x, y, spray);
  pc.ellipse(-24, 5, 4, 1.6, spray);
  // Turn the statue to face the other way, same convention as every box-faced item.
  if (frontFace(rotation) === 'right') pc.flipX();
};

const drawHdbBlock: FurniDrawer = (pc, rotation) => {
  const wall = faces('#f4e1c1', '#fff1d8');
  const trim = faces('#e8805a', '#f59b72');
  drawBox(pc, box(-0.36, 0.36, -0.22, 0.22, 0, 12, faces('#bdb5a6')));
  drawBox(pc, box(-0.36, 0.36, -0.22, 0.22, 12, 84, wall));
  drawBox(pc, box(-0.38, 0.38, -0.24, 0.24, 84, 88, trim));
  // Void deck pillars and shadow.
  faceQuad(pc, 'left', 0.22, -0.3, 0.3, 0, 10, '#4a4238');
  faceQuad(pc, 'right', 0.36, -0.16, 0.16, 0, 10, '#3a3228');
  faceQuad(pc, 'left', 0.22, -0.02, 0.02, 0, 10, '#bdb5a6');
  // Windows and a painted stripe on each visible face.
  for (let h = 16; h < 80; h += 8) {
    for (let s = -0.3; s < 0.3; s += 0.12) faceQuad(pc, 'left', 0.22, s, s + 0.07, h, h + 4, '#6a8fb0');
    for (let s = -0.18; s < 0.18; s += 0.12) faceQuad(pc, 'right', 0.36, s, s + 0.07, h, h + 4, '#56789a');
  }
  faceQuad(pc, 'left', 0.22, -0.36, 0.36, 46, 49, '#e8805a');
  faceQuad(pc, 'right', 0.36, -0.22, 0.22, 46, 49, '#d06a48');
  // Block number.
  const plate = iso(0.37, 0, 70);
  pc.rect(plate.x - 1, plate.y - 3, 6, 5, '#2a4a9a');
  text(pc, '1', plate.x, plate.y - 3, '#fffaf0');
  if (frontFace(rotation) === 'right') pc.flipX();
};

const TERRAZZO = faces('#cfc6b8', '#ddd5c8');

function terrazzoSpeckle(pc: PixelCanvas, seed: number): void {
  sprinkle(pc, seed, -24, -40, 48, 40, ['#b0a696', '#e8e1d4', '#9a8f7e'], 22);
}

const drawVoidDeckBench: FurniDrawer = (pc, rotation) => {
  drawBox(pc, box(-0.22, 0.22, -0.22, 0.22, 0, 10, faces('#b0a696')));
  drawBoxes(pc, seatWithBack(rotation, 0.4, [10, 15], 30, 0.1, TERRAZZO, TERRAZZO));
  terrazzoSpeckle(pc, rotation + 3);
};

const drawMrtBench: FurniDrawer = (pc, rotation) => {
  const steel = faces('#a9b0b7', '#c7cdd2');
  const seat = faces('#3d9e8c', '#56b3a4');
  for (const end of [-0.32, 0.26]) {
    const leg = runsAlongU(rotation) ? box(end, end + 0.06, -0.3, 0.3, 0, 12, steel) : box(-0.3, 0.3, end, end + 0.06, 0, 12, steel);
    drawBox(pc, leg);
  }
  drawBoxes(pc, [runBox(rotation, 0.34, 12, 15, seat, 0.46), backRest(rotation, 0.34, 0.06, 15, 32, seat, 0.46)]);
  // Perforations on the seat.
  const rand = seededRandom(rotation + 9);
  for (let i = 0; i < 10; i += 1) {
    const p = iso((rand() - 0.5) * 0.7, (rand() - 0.5) * 0.4, 15);
    pc.px(p.x, p.y, '#2c7a6b');
  }
};

const drawVoidDeckTable: FurniDrawer = (pc) => {
  drawCylinder(pc, 0.12, 0, 22, '#cfc6b8', '#b0a696');
  drawCylinder(pc, 0.4, 22, 27, '#ddd5c8', '#b0a696');
  // Chessboard inlaid in the top.
  const n = 6;
  for (let i = 0; i < n; i += 1) {
    for (let j = 0; j < n; j += 1) {
      if ((i + j) % 2) continue;
      const u0 = -0.24 + (i * 0.48) / n;
      const v0 = -0.24 + (j * 0.48) / n;
      const s = 0.48 / n;
      pc.poly([iso(u0, v0, 27), iso(u0 + s, v0, 27), iso(u0 + s, v0 + s, 27), iso(u0, v0 + s, 27)], '#6b5a48');
    }
  }
};

const drawDragonSlide: FurniDrawer = (pc, rotation) => {
  const tiles = ['#f0bf36', '#ef7d2c', '#fffaf0', '#b8352e'];
  // Three humps along the body: arches you can crawl under.
  for (const [u, h] of [
    [0.34, 16],
    [0.06, 24],
    [-0.22, 20],
  ] as const) {
    const c = iso(u, 0.05, 0);
    pc.ellipse(c.x, c.y - h * 0.55, 9, h * 0.6, '#d9433b');
    pc.ellipse(c.x + 2, c.y - h * 0.45, 3, h * 0.5, '#b8352e');
    pc.ellipse(c.x, c.y - 1, 5, h * 0.35, '#00000000');
  }
  // Tail tip at the front.
  const tail = iso(0.48, 0.05, 0);
  pc.poly(
    [
      { x: tail.x - 4, y: tail.y - 6 },
      { x: tail.x + 6, y: tail.y - 2 },
      { x: tail.x - 2, y: tail.y },
    ],
    '#d9433b',
  );
  // The head at the back, with a snout, eye, horns and a toothy grin.
  const head = iso(-0.42, -0.02, 22);
  pc.rect(head.x - 8, head.y - 14, 16, 14, '#d9433b');
  pc.rect(head.x - 16, head.y - 8, 9, 8, '#d9433b');
  pc.rect(head.x + 3, head.y - 14, 5, 14, '#b8352e');
  pc.rect(head.x - 4, head.y - 12, 3, 3, '#fffaf0');
  pc.px(head.x - 4, head.y - 11, '#1d1d1d');
  pc.rect(head.x - 16, head.y - 2, 9, 1, '#fffaf0');
  for (let x = head.x - 15; x < head.x - 7; x += 2) pc.px(x, head.y - 1, '#fffaf0');
  pc.rect(head.x - 3, head.y - 20, 2, 6, '#f0bf36');
  pc.rect(head.x + 3, head.y - 19, 2, 5, '#f0bf36');
  pc.rect(head.x - 14, head.y - 10, 2, 2, '#1d1d1d');
  // Support tower under the head.
  const foot = iso(-0.42, -0.02, 0);
  pc.rect(foot.x - 5, foot.y - 22, 10, 22, '#d9433b');
  pc.rect(foot.x - 3, foot.y - 18, 6, 18, '#3a2a24');
  // Mosaic tiles over all the red.
  sprinkle(pc, 11, -40, -52, 80, 56, tiles, 260);
  // A steel slide chute sweeping down from the head.
  pc.poly(
    [
      { x: head.x + 6, y: head.y - 4 },
      { x: head.x + 10, y: head.y - 1 },
      { x: 14, y: 14 },
      { x: 8, y: 14 },
    ],
    '#c7cdd2',
  );
  pc.line(head.x + 7, head.y - 3, 9, 13, '#e8eef2');
  if (frontFace(rotation) === 'right') pc.flipX();
};

const drawDurianStall: FurniDrawer = (pc, rotation) => {
  const crate = faces('#b07a48', '#c99562');
  drawBox(pc, box(-0.44, 0.44, -0.44, 0.44, 0, 22, crate));
  faceQuad(pc, 'left', 0.44, -0.44, 0.44, 10, 12, '#8a5a34');
  faceQuad(pc, 'right', 0.44, -0.44, 0.44, 10, 12, '#6b4226');
  const rand = seededRandom(21);
  for (const [u, v] of [
    [-0.2, -0.2],
    [0.15, -0.2],
    [-0.2, 0.15],
    [0.15, 0.15],
    [0, 0],
  ] as const) {
    const p = iso(u, v, 22 + (u === 0 ? 8 : 0));
    pc.ellipse(p.x, p.y - 5, 7, 6, '#7a8a2a');
    pc.ellipse(p.x - 2, p.y - 7, 3, 2, '#a0b040');
    for (let i = 0; i < 8; i += 1) pc.px(p.x - 6 + Math.floor(rand() * 12), p.y - 10 + Math.floor(rand() * 9), '#4a5a1a');
  }
  // Price sign on whichever corner currently faces the customer; the text itself
  // stays upright (flipping the whole canvas would mirror it backwards).
  const signCorner = frontFace(rotation) === 'right' ? iso(-0.44, -0.3, 30) : iso(0.44, -0.3, 30);
  pc.rect(signCorner.x - 1, signCorner.y - 14, 1, 14, '#6b4226');
  pc.rect(signCorner.x - 8, signCorner.y - 22, 16, 8, '#fffaf0');
  text(pc, '$10', signCorner.x - 7, signCorner.y - 21, '#d9433b');
};

const drawOrchidPot: FurniDrawer = (pc) => {
  drawCylinder(pc, 0.15, 0, 12, '#1f3a6b', '#e8eef5');
  const rim = iso(0, 0, 12);
  pc.rect(rim.x - 9, rim.y - 1, 18, 1, '#3a6fd6');
  // Leaves, stem and a spray of purple Vanda Miss Joaquim blooms.
  pc.poly(
    [
      { x: -1, y: -12 },
      { x: -12, y: -18 },
      { x: -2, y: -16 },
    ],
    '#3d9e4c',
  );
  pc.poly(
    [
      { x: 1, y: -12 },
      { x: 12, y: -17 },
      { x: 2, y: -15 },
    ],
    '#2f7f3c',
  );
  pc.line(0, -12, 3, -40, '#3d7a3c');
  for (const [x, y] of [
    [5, -40],
    [-3, -34],
    [7, -30],
    [-2, -24],
  ] as const) {
    pc.ellipse(x, y, 4, 3, '#b56ad0');
    pc.ellipse(x, y + 1, 2, 1.5, '#e8a0e0');
    pc.px(x, y, '#f0bf36');
  }
};

const drawSgFlag: FurniDrawer = (pc, rotation) => {
  drawCylinder(pc, 0.12, 0, 3, '#8b9299', '#6b727a');
  pc.rect(-1, -78, 2, 76, '#c7cdd2');
  pc.rect(0, -78, 1, 76, '#a9b0b7');
  pc.ellipse(0, -79, 2, 2, '#f0bf36');
  // Red over white, hung along the room's x axis: every two pixels across it
  // drops one pixel, so its edges run parallel to the tile edges instead of
  // sitting flat like a sticker. As on the real flag the crescent bulges
  // towards the pole and opens towards the fly, with five stars in its mouth.
  const drop = (x: number) => Math.floor(x / 2);
  const moon = (x: number, y: number) => (x - 7.5) ** 2 + (y + 71.5) ** 2 <= 20 && (x - 9.9) ** 2 + (y + 71.5) ** 2 > 11;
  const stars = new Set(['13,-75', '16,-73', '15,-69', '11,-69', '10,-73']);
  for (let x = 1; x < 29; x += 1) {
    const dy = drop(x);
    pc.rect(x, -76 + dy, 1, 9, '#e0283a');
    pc.rect(x, -67 + dy, 1, 9, '#fffaf0');
    for (let y = -76; y < -67; y += 1) {
      if (moon(x, y) || stars.has(`${x},${y}`)) pc.px(x, y + dy, '#fffaf0');
    }
  }
  if (frontFace(rotation) === 'right') pc.flipX();
};

const drawRedLanterns: FurniDrawer = (pc, rotation) => {
  const wood = faces('#6b4226');
  const alongU = runsAlongU(rotation);
  const posts: BoxPiece[] = [-0.4, 0.34].map((s) =>
    alongU ? box(s, s + 0.06, -0.03, 0.03, 0, 64, wood) : box(-0.03, 0.03, s, s + 0.06, 0, 64, wood),
  );
  posts.push(alongU ? box(-0.4, 0.4, -0.03, 0.03, 60, 64, wood) : box(-0.03, 0.03, -0.4, 0.4, 60, 64, wood));
  drawBoxes(pc, posts);
  for (const s of [-0.18, 0.18]) {
    const top = alongU ? iso(s, 0, 60) : iso(0, s, 60);
    pc.rect(top.x, top.y, 1, 6, '#3a2a24');
    pc.ellipse(top.x, top.y + 15, 7, 9, '#d9433b');
    pc.ellipse(top.x - 2, top.y + 13, 2, 5, '#f26a5a');
    pc.rect(top.x - 4, top.y + 5, 9, 2, '#f0bf36');
    pc.rect(top.x - 4, top.y + 23, 9, 2, '#f0bf36');
    pc.rect(top.x, top.y + 25, 1, 5, '#f0bf36');
  }
};

const PERANAKAN = ['#2c9f9b', '#f19bb7', '#f0bf36', '#fffaf0'];

const drawPeranakanRug: FurniDrawer = (pc) => {
  const n = 3;
  const size = 0.94 / n;
  for (let i = 0; i < n; i += 1) {
    for (let j = 0; j < n; j += 1) {
      const u0 = -0.47 + i * size;
      const v0 = -0.47 + j * size;
      const cell = (inset: number, color: string) =>
        pc.poly(
          [
            iso(u0 + inset, v0 + inset),
            iso(u0 + size - inset, v0 + inset),
            iso(u0 + size - inset, v0 + size - inset),
            iso(u0 + inset, v0 + size - inset),
          ],
          color,
        );
      cell(0, '#fffaf0');
      cell(0.02, PERANAKAN[(i + j) % 2 ? 0 : 1]!);
      cell(0.08, '#fffaf0');
      cell(0.11, PERANAKAN[(i * 2 + j) % 3 === 0 ? 2 : 1]!);
      const c = iso(u0 + size / 2, v0 + size / 2);
      pc.px(c.x, c.y, '#2c9f9b');
    }
  }
};

const drawKopitiamSign: FurniDrawer = (pc) => {
  const post = faces('#3a3a42');
  drawBox(pc, box(-0.42, -0.36, -0.03, 0.03, 0, 58, post));
  drawBox(pc, box(0.36, 0.42, -0.03, 0.03, 0, 58, post));
  // The board faces the viewer so the lettering stays readable.
  pc.rect(-24, -74, 48, 20, '#c8231f');
  pc.rect(-24, -74, 48, 2, '#f0bf36');
  pc.rect(-24, -56, 48, 2, '#f0bf36');
  text(pc, 'KOPITIAM', -16, -69, '#ffe58a');
  pc.px(-20, -63, '#fffaf0');
  pc.px(20, -63, '#fffaf0');
};

const SNACKS = ['#d9433b', '#f0bf36', '#3a6fd6', '#3d9e4c', '#ef7d2c', '#f19bb7', '#fffaf0'];

const drawMamaShelf: FurniDrawer = (pc, rotation) => {
  const wood = faces('#8a5a34', '#a8703f');
  const alongV = rotation === 2 || rotation === 6;
  drawBox(pc, alongV ? box(-0.22, 0.22, -0.46, 0.46, 0, 58, wood) : box(-0.46, 0.46, -0.22, 0.22, 0, 58, wood));
  const face = frontFace(rotation);
  // Candy jars on top.
  for (const s of [-0.25, 0.05, 0.3]) {
    const p = alongV ? iso(0, s, 58) : iso(s, 0, 58);
    pc.rect(p.x - 3, p.y - 8, 6, 8, '#dff0f4');
    pc.rect(p.x - 3, p.y - 10, 6, 2, '#d9433b');
    pc.rect(p.x - 2, p.y - 5, 4, 4, SNACKS[Math.abs(Math.round(s * 10)) % SNACKS.length]!);
  }
  if (!face) return;
  const at = 0.22;
  faceQuad(pc, face, at, -0.42, 0.42, 3, 56, '#4a3020');
  const rand = seededRandom(rotation + 31);
  for (const base of [4, 22, 40]) {
    faceQuad(pc, face, at, -0.44, 0.44, base - 2, base, '#b17a48');
    let s = -0.4;
    while (s < 0.36) {
      const w = 0.07 + rand() * 0.05;
      faceQuad(pc, face, at, s, s + w, base, base + 8 + Math.floor(rand() * 6), SNACKS[Math.floor(rand() * SNACKS.length)]!);
      s += w + 0.015;
    }
  }
};

const drawSatayGrill: FurniDrawer = (pc, rotation) => {
  const metal = faces('#3a3a42', '#55555f');
  drawBoxes(pc, [runBox(rotation, 0.2, 0, 18, metal, 0.46)]);
  const alongU = runsAlongU(rotation);
  // Glowing coals, then sticks of satay across the top.
  const coal = alongU ? [iso(-0.4, -0.14, 18), iso(0.4, -0.14, 18), iso(0.4, 0.14, 18), iso(-0.4, 0.14, 18)] : [iso(-0.14, -0.4, 18), iso(0.14, -0.4, 18), iso(0.14, 0.4, 18), iso(-0.14, 0.4, 18)];
  pc.poly(coal, '#ef7d2c');
  sprinkle(pc, 4, -14, -26, 28, 10, ['#ffd24a', '#d9433b'], 20);
  for (let i = -3; i <= 3; i += 1) {
    const s = i * 0.11;
    const a = alongU ? iso(s, -0.26, 20) : iso(-0.26, s, 20);
    const b = alongU ? iso(s, 0.26, 20) : iso(0.26, s, 20);
    pc.line(a.x, a.y, b.x, b.y, '#c9a26a');
    const m = alongU ? iso(s, 0, 21) : iso(0, s, 21);
    pc.rect(m.x - 1, m.y - 2, 3, 3, '#8a4a22');
  }
  // Smoke.
  for (const [x, y] of [
    [-3, -30],
    [2, -36],
    [-1, -42],
    [4, -46],
  ] as const) {
    pc.ellipse(x, y, 3, 2, '#d8d4cc88');
  }
};

const drawBirdCage: FurniDrawer = (pc) => {
  drawCylinder(pc, 0.14, 0, 2, '#6b4226', '#4a2c18');
  pc.rect(-1, -66, 2, 64, '#8a5a34');
  pc.rect(-1, -66, 12, 2, '#8a5a34');
  pc.rect(9, -64, 1, 4, '#3a2a24');
  // Bamboo cage: a dome of bars around a yellow songbird.
  const cx = 9;
  const cy = -46;
  pc.ellipse(cx, cy - 12, 9, 4, '#c9a26a');
  pc.rect(cx - 9, cy + 6, 19, 2, '#a8803a');
  for (let x = cx - 8; x <= cx + 8; x += 3) pc.rect(x, cy - 12, 1, 18, '#c9a26a');
  pc.ellipse(cx, cy, 3, 2.5, '#f0bf36');
  pc.px(cx + 2, cy - 1, '#1d1d1d');
  pc.px(cx + 3, cy, '#ef7d2c');
  pc.rect(cx - 6, cy + 3, 12, 1, '#8a5a34');
};

const drawKuehDisplay: FurniDrawer = (pc, rotation) => {
  drawBox(pc, box(-0.42, 0.42, -0.42, 0.42, 0, 12, faces('#8a5a34', '#a8703f')));
  drawBox(pc, box(-0.4, 0.4, -0.4, 0.4, 12, 30, faces('#cfe8f0', '#e8f6fa')));
  faceQuad(pc, 'left', 0.4, -0.3, -0.05, 18, 28, '#f4fbfd');
  // The kueh, seen through the glass lid.
  const kueh: Array<[number, number, string]> = [
    [-0.2, -0.2, '#d9433b'],
    [0.05, -0.2, '#d9433b'],
    [-0.2, 0.08, '#3d9e4c'],
    [0.05, 0.08, '#3d9e4c'],
    [0.22, -0.05, '#f19bb7'],
  ];
  for (const [u, v, color] of kueh) {
    const p = iso(u, v, 30);
    pc.ellipse(p.x, p.y - 1, 4, 2.5, color);
    pc.px(p.x - 1, p.y - 2, shade(color, 1.4));
  }
  const lapis = iso(0.22, 0.2, 30);
  for (let i = 0; i < 4; i += 1) pc.rect(lapis.x - 3, lapis.y - 4 + i, 6, 1, i % 2 ? '#8a3a2a' : '#f0bf36');
  if (frontFace(rotation) === 'right') pc.flipX();
};

const drawChopeTable: FurniDrawer = (pc) => {
  drawCylinder(pc, 0.2, 0, 2, '#4a4a52', '#34343c');
  drawCylinder(pc, 0.05, 2, 26, '#4a4a52', '#3a3a42');
  drawCylinder(pc, 0.44, 26, 30, '#efebe2', '#4a4a52');
  sprinkle(pc, 7, -14, -36, 28, 10, ['#cfc9bc', '#dcd7cb'], 18);
  // The tissue packet that says "this seat is taken".
  const p = iso(0.02, 0.02, 30);
  pc.poly(
    [
      { x: p.x - 5, y: p.y - 2 },
      { x: p.x + 1, y: p.y - 5 },
      { x: p.x + 7, y: p.y - 2 },
      { x: p.x + 1, y: p.y + 1 },
    ],
    '#fffaf0',
  );
  pc.px(p.x + 1, p.y - 2, '#3a6fd6');
  pc.px(p.x, p.y - 2, '#3a6fd6');
};

const drawRainTree: FurniDrawer = (pc) => {
  drawCylinder(pc, 0.1, 0, 58, '#7a5230', '#5a3a20');
  pc.line(0, -50, -14, -64, '#6b4630');
  pc.line(1, -52, 14, -66, '#6b4630');
  // A broad umbrella canopy: dark underside, lighter crown.
  pc.ellipse(0, -66, 40, 12, '#2f6f35');
  pc.ellipse(-12, -76, 20, 10, '#3d8f45');
  pc.ellipse(12, -78, 22, 10, '#3d8f45');
  pc.ellipse(0, -86, 24, 10, '#56b35f');
  sprinkle(pc, 13, -34, -92, 68, 26, ['#6fca6f', '#2f7f3c', '#8fdf7a'], 60);
};

const drawBicycle: FurniDrawer = (pc, rotation) => {
  const alongU = runsAlongU(rotation);
  const at = (s: number, h: number) => (alongU ? iso(s, 0, h) : iso(0, s, h));
  const wheel = (s: number) => {
    const c = at(s, 10);
    pc.ellipse(c.x, c.y, 9, 9, '#2a2a33');
    pc.ellipse(c.x, c.y, 7, 7, '#00000000');
    for (let i = 0; i < 4; i += 1) {
      const a = (i / 4) * Math.PI;
      pc.line(c.x - Math.cos(a) * 6, c.y - Math.sin(a) * 6, c.x + Math.cos(a) * 6, c.y + Math.sin(a) * 6, '#a9b0b7');
    }
  };
  wheel(-0.3);
  wheel(0.3);
  const back = at(-0.3, 10);
  const front = at(0.3, 10);
  const seat = at(-0.1, 26);
  const bar = at(0.24, 30);
  pc.line(back.x, back.y, seat.x, seat.y, '#d9433b');
  pc.line(seat.x, seat.y, bar.x, bar.y - 2, '#d9433b');
  pc.line(back.x, back.y, front.x - 4, front.y - 8, '#d9433b');
  pc.line(bar.x, bar.y, front.x, front.y, '#d9433b');
  pc.rect(seat.x - 3, seat.y - 2, 6, 2, '#1d1d1d');
  pc.rect(bar.x - 3, bar.y - 3, 7, 2, '#3a3a42');
};

/* -------------------------------------------------------------------------- */

export const SINGAPORE_DRAWERS: Record<string, FurniDrawer> = {
  couch_left: drawCouchLeft,
  couch_middle: drawCouchMiddle,
  couch_right: drawCouchRight,
  bar_stool: drawBarStool,
  coffee_table: drawCoffeeTable,
  cafe_window: drawCafeWindow,
  stage_floor: drawStageFloor,
  mic_stand: drawMicStand,
  chalkboard_menu: drawChalkboard,
  merlion: drawMerlion,
  hdb_block: drawHdbBlock,
  void_deck_bench: drawVoidDeckBench,
  void_deck_table: drawVoidDeckTable,
  mrt_bench: drawMrtBench,
  dragon_slide: drawDragonSlide,
  durian_stall: drawDurianStall,
  orchid_pot: drawOrchidPot,
  sg_flag: drawSgFlag,
  red_lanterns: drawRedLanterns,
  peranakan_rug: drawPeranakanRug,
  kopitiam_sign: drawKopitiamSign,
  mama_shop_shelf: drawMamaShelf,
  satay_grill: drawSatayGrill,
  bird_cage: drawBirdCage,
  kueh_display: drawKuehDisplay,
  chope_table: drawChopeTable,
  rain_tree: drawRainTree,
  bicycle: drawBicycle,
};

// Unused-import guard for helpers kept for future items.
void HH;
void HW;
